import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckinState,
  CheckinStep,
  getRules,
  getRulesPdfUrl,
  HouseRules,
  signRules,
} from '../../../../api/stay-checkin-client';
import { StayPublicError } from '../../../../api/stays-public-client';
import { Shake, StateSwitch, StatusLabel } from '../../../../motion';
import { SignaturePad, SignatureValue } from '../../../shared/signature-pad';
import { ErrorText, FieldError } from '../../../onboarding/onboarding.styled';
import { Actions, CheckRow, Primary, Quiet, Secondary } from '../../../onboarding/stay/public-stay-page.styled';
import { LoadingFill } from '../../../onboarding/stay/stay-steps';
import { t } from '../../../onboarding/stay/stay-strings';
import { Note, RulesText, Section, StepHeading } from './guest-checkin.styled';

const L = 'en' as const;

type RulesLoad = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; rules: HouseRules };

/**
 * STEP 3 — HOUSE RULES (plan S6, D-step4): the published version, a typed or
 * drawn signature (the shared SignaturePad), the agreement, "Sign house
 * rules". The server renders the signed PDF and e-mails it; nothing of an
 * unsigned drawing is kept (a reload shows an empty pad). A changed version
 * (409 RULES_CHANGED) is read again and shown.
 */
export const RulesStep: React.FC<{
  token: string;
  state: CheckinState;
  onSigned: (steps: CheckinStep[], completedAt: string | null) => void;
  onError: (err: unknown) => string | null;
}> = ({ token, state, onSigned, onError }) => {
  const [load, setLoad] = useState<RulesLoad>({ kind: 'loading' });
  const [sig, setSig] = useState<SignatureValue | null>(null);
  const [agree, setAgree] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [signed, setSigned] = useState(!!state.signed_at);

  const read = useCallback(() => {
    setLoad({ kind: 'loading' });
    getRules(token)
      .then(r => {
        if ('none' in r) setLoad({ kind: 'error' });
        else setLoad({ kind: 'ready', rules: r });
      })
      .catch(() => setLoad({ kind: 'error' }));
  }, [token]);

  useEffect(() => {
    if (!signed) read();
  }, [read, signed]);

  const download = async () => {
    try {
      const { url } = await getRulesPdfUrl(token);
      window.location.assign(url);
    } catch (err) {
      setProblem(onError(err));
    }
  };

  if (signed) {
    return (
      <>
        <StepHeading>{t(L, 'rulesSigned')}</StepHeading>
        <Note role="status">{t(L, 'signedCopy')}</Note>
        {problem ? <ErrorText>{problem}</ErrorText> : null}
        <Actions>
          <Secondary type="button" onClick={download}>
            {t(L, 'downloadCopy')}
          </Secondary>
        </Actions>
      </>
    );
  }

  const submit = async () => {
    if (busy || load.kind !== 'ready') return;
    setFields({});
    setProblem(null);
    if (!sig) {
      setFields({ signature: t(L, 'sigEmpty') });
      setAttempt(a => a + 1);

      return;
    }
    if (!agree) {
      setFields({ agree: t(L, 'agreeRequired') });
      setAttempt(a => a + 1);

      return;
    }
    setBusy(true);
    try {
      const base = {
        rules_id: load.rules.rules_id,
        body_sha256: load.rules.body_sha256,
        consent_version: load.rules.consent_version,
      };
      const out = sig.kind === 'drawn'
        ? await signRules(token, { ...base, kind: 'drawn', typed_name: state.guest_first_name, drawn_png: sig.dataUrl })
        : await signRules(token, { ...base, kind: 'typed', typed_name: sig.name });
      setSigned(true);
      onSigned(out.steps, out.completed_at);
    } catch (err) {
      setBusy(false);
      setAttempt(a => a + 1);
      if (err instanceof StayPublicError && err.code === 'RULES_ALREADY_SIGNED') {
        // A double tap: the first one signed. Read the state again as success.
        setSigned(true);
        onSigned(state.steps, state.completed_at);

        return;
      }
      if (err instanceof StayPublicError && err.code === 'RULES_CHANGED') {
        setProblem(onError(err));
        // The pad remounts empty, so a drawing must not survive in state; the
        // new version needs a fresh agreement too. A typed name comes back.
        if (sig.kind === 'drawn') setSig(null);
        setAgree(false);
        read();

        return;
      }
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        setFields(err.fields);

        return;
      }
      setProblem(onError(err));
    }
  };

  return (
    <Shake active={attempt > 0} attempt={attempt}>
      <StepHeading>{t(L, 'rulesTitle')}</StepHeading>
      <StateSwitch state={load.kind === 'ready' ? 'ready' : load.kind === 'error' ? 'error' : 'loading'}>
        {load.kind === 'loading' ? <LoadingFill /> : null}
        {load.kind === 'error' ? (
          <>
            <ErrorText>{t(L, 'error')}</ErrorText>
            <Secondary type="button" onClick={read}>
              {t(L, 'tryAgain')}
            </Secondary>
          </>
        ) : null}
        {load.kind === 'ready' ? (
          <>
            <Quiet>{t(L, 'rulesVersion', { n: load.rules.version })}</Quiet>
            <RulesText tabIndex={0} aria-label={t(L, 'rulesTitle')} data-rules-body="">
              {load.rules.body}
            </RulesText>
            <Section>
              <SignaturePad
                value={sig}
                onChange={setSig}
                name={state.guest_first_name}
                disabled={busy}
                labels={{
                  typeTab: t(L, 'sigTypeTab'),
                  drawTab: t(L, 'sigDrawTab'),
                  typeLabel: t(L, 'sigTypeLabel'),
                  drawHint: t(L, 'sigDrawHint'),
                  clear: t(L, 'sigClear'),
                  empty: t(L, 'sigEmpty'),
                }}
              />
              <FieldError message={fields.signature || fields.typed_name || fields.drawn_png} />
            </Section>
            <CheckRow htmlFor="rules-agree">
              <input id="rules-agree" type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />
              <span>{t(L, 'agreeRules')}</span>
            </CheckRow>
            <FieldError message={fields.agree} />
            {problem ? <ErrorText>{problem}</ErrorText> : null}
            <Actions>
              <Primary type="button" onClick={submit} aria-busy={busy || undefined}>
                <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'signRules'), busy: t(L, 'signing') }} />
              </Primary>
            </Actions>
          </>
        ) : null}
      </StateSwitch>
    </Shake>
  );
};
